<template>
    <group-report-template  v-model:current-report-template-id="currentReportTemplateId"
                            :template-id="templateId"
                            v-model:for-group="forGroup"
                            v-model:interval="interval"
                            v-model:ran-report="reportSuccessfullySent"
                            v-model:reload-data="reloadTable"
                            v-model:running-errors="errorSending"
                            v-model:running-report="runningReport"
                            v-model:settings="settings"
                            :can-edit="permissions.can('manage_compliance_achievement_report')"
                            :users="users"
                            :default-settings="defaultSettings"
                            :hide-title="true"
                            :use-settings-drawer="true"
                            :use-template-cards="true"
                            :use-group-reporting-option="groupReportingEnabled"
                            :has-settings="hasManualRunSettings"
                            :report-icon="settingsSchema.icon"
                            :schema="settingsSchema"
                            report-name="threshold-achievement-report"
                            v-model:display-section="displaySection"
                            @useStandardReporting="setUseStandardReporting"
    >

        <template   #create-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="true"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #edit-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="displaySection === 'show-template' || useStandardReporting"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #run-options="props">
            <!-- The report scores every point the user can see, so there is no
                 point selection: just the date range (shared selector, no meters)
                 and the pass threshold (edited in the Report Settings drawer,
                 posted via extraParams). -->
            <interval-selection   :use-range="! props.reportTemplateId"
                                        :use-meters="false"
                                        :footer-mode="props.footerMode ?? 'none'"
                                        v-model:range-start="rangeStart"
                                        v-model:range-end="rangeEnd"
                                        :min-date="minDate"
                                        :max-date="maxDate"
                                        :is-running="runningReport"
                                        :ran-report="reportSuccessfullySent"
                                        :running-errors="errorSending"
                                        :timezone="timezone"
                                        @run-report="runReport(props.reportTemplateId ?? null)"
            ></interval-selection>
        </template>
    </group-report-template>
</template>

<script>
import {ref} from 'vue'

import {SonicuCoreMixin} from "../../mixins/SonicuCoreMixin";
import {ajaxTableMixin} from "../../mixins/ajaxTableMixin";
import {useGroupReport} from "@/composables/useGroupReport";

import GroupReportTemplate from "./GroupReportTemplate";
import IntervalSelection from "./IntervalSelection";
import ReportSettingsPanel from "./settings/ReportSettingsPanel.vue";
import thresholdAchievementSettingsSchema from "./settings/schemas/threshold-achievement";
import {toReportDate} from "./settings/ReportDateRange.vue";

export default {
    name: "GroupThresholdAchievementReport",
    mixins: [SonicuCoreMixin, ajaxTableMixin],
    components: {
        GroupReportTemplate,
        IntervalSelection,
        ReportSettingsPanel,
    },
    props: {
        // null on the report page; 'new' or a group report id on its New or
        // Edit page.
        templateId: {
            type: [String, Number],
            default: null,
        },
        interval: {
            type: String,
            default: 'monthly',
        },
        minDate: {
            type: [Date, String],
        },
        maxDate: {
            type: [Date, String],
        },
        startDate: {
            type: [Date, String],
            default: null,
        },
        defaultSettings: {
            type: Object,
            default() {
                return {}
            }
        },
        users: {
            type: Object,
            default() {
                return {}
            }
        },
        timezone: {
            type: String,
            default: 'America/New_York',
        },
    },
    setup(props) {
        // Explicit start/end from the shared range selector. The report takes no
        // meters, so extraParams posts only start/end (via toReportDate) plus the
        // pass threshold the drawer edits. A saved template's manual run posts no
        // start, so the controller derives the window from the template's
        // interval, the same window its scheduled sends cover.
        const rangeStart = ref(null)
        const rangeEnd = ref(null)

        const report = useGroupReport(thresholdAchievementSettingsSchema, {
            interval: props.interval,
            defaultSettings: props.defaultSettings,
            extraParams: (state, reportTemplateId) => {
                const params = {
                    pass_threshold: state.settings.value['passThreshold'],
                }

                if (reportTemplateId === null) {
                    params.start = toReportDate(rangeStart.value, props.timezone)
                    params.end = toReportDate(rangeEnd.value, props.timezone)
                }

                return params
            },
        })

        return {
            ...report,
            rangeStart,
            rangeEnd,
        }
    },
    computed: {
        settingsSchema() {
            return thresholdAchievementSettingsSchema
        },
    },
}
</script>
